import { BLANK_ADDRESS } from '../geo/addressUtils';
import { SHIPPING_METHOD_OPTIONS } from '../../utils/catalogs';
import { resolvePhoneCountryIso2 } from '../../utils/phone';

/**
 * Steps of the seller creation wizard, in order.
 *
 * The email gets a step of its own even though it is a single field: it is the
 * only value in the form that cannot be taken back — it opens a user account
 * and sends that account its password — and the only one whose collision
 * leaves rows behind on the server. Validating it on its own, before anything
 * else is filled in, is the point of the split.
 */
export const SELLER_STEPS = [
  { id: 'company', label: 'Empresa' },
  { id: 'account', label: 'Cuenta' },
  { id: 'address', label: 'Dirección' },
  { id: 'warehouses', label: 'Bodegas' },
  { id: 'summary', label: 'Resumen' },
];

export const SUMMARY_STEP_INDEX = SELLER_STEPS.length - 1;

/** Ceiling the legacy form already enforced, kept as the agreed limit. */
export const MAX_WAREHOUSES = 10;

/**
 * Plans on offer.
 *
 * `PlanEnum` on the backend also carries `omnichannel` and `full_omnichannel`,
 * commented out in the legacy form. Kept at parity here; opening them is a
 * matter of adding the entries.
 */
export const PLAN_OPTIONS = [{ value: 'delivery', label: 'Delivery' }];

export const DEFAULT_PLAN = PLAN_OPTIONS[0].value;

/**
 * Shipping methods a warehouse can be attached to a zone with.
 *
 * The shared catalog carries two "not selected" entries, and the uppercase one
 * is not part of the backend enum at all — sending it fails the whole create
 * with `Shipping method 'NOT_SELECTED' not found`. Neither belongs in a form
 * that is choosing how a warehouse ships, so both are dropped.
 */
export const WAREHOUSE_SHIPPING_METHOD_OPTIONS = SHIPPING_METHOD_OPTIONS.filter(
  (option) => option.value.toLowerCase() !== 'not_selected'
);

/** Backend default when no capacity is sent, shown as the placeholder. */
export const DEFAULT_WAREHOUSE_CAPACITY = 1000;

/**
 * Identity of a warehouse card while the form is open.
 *
 * A counter rather than `crypto.randomUUID`, which is only defined in a secure
 * context — serving the dev build over a LAN IP would leave it undefined and
 * take the page down on mount. These ids never leave the browser: they key the
 * list and address a card, nothing more.
 */
let warehouseSequence = 0;
const nextWarehouseId = () => `warehouse-${(warehouseSequence += 1)}`;

/**
 * A blank warehouse card.
 *
 * Built by a factory rather than exported as a constant because each card
 * carries its own `id`: keying the list by array index —as the legacy does—
 * leaves the wrong state behind when a card in the middle is removed.
 *
 * @param {string} countryIso2 - Account country, seeding both phone pickers
 */
export const createBlankWarehouse = (countryIso2) => ({
  id: nextWarehouseId(),
  name: '',
  storeKeeper: '',
  phone: '',
  phoneCountry: resolvePhoneCountryIso2(countryIso2),
  email: '',
  customerPhone: '',
  customerPhoneCountry: resolvePhoneCountryIso2(countryIso2),
  capacity: '',
  shippingZone: '',
  shippingMethod: '',
  comments: '',
  allDayPickup: true,
  amPickupFrom: '',
  amPickupTo: '',
  pmPickupFrom: '',
  pmPickupTo: '',
  ...BLANK_ADDRESS,
});

/**
 * A blank form. The commercial address is spread in flat, the shape
 * `AddressFields` expects to read and patch.
 *
 * @param {string} countryIso2
 */
export const createBlankSellerForm = (countryIso2) => ({
  razonSocial: '',
  taxId: '',
  plan: DEFAULT_PLAN,
  email: '',
  ...BLANK_ADDRESS,
  warehouses: [createBlankWarehouse(countryIso2)],
});
